import {
  IClientPatch,
  IPatchAddAction,
  IPatchSubstAction,
  PatchType,
} from "../../../components/course-author/helpers/patches";
import { addPatch, createPatchBase, substPatch } from "../../../components/course-author/helpers/patches/creators";
import { ICourseLocalizedText as LocalizedText } from "../../../components/course-author/types";
import { PreferredLocale } from "../../../utils/locales";
import { IScormPackage } from "../../types/common";

export function modifyScormPackageTitlePatch(
  scormPackage: IScormPackage,
  userId: string,
  title: string
): IClientPatch<IPatchSubstAction<string>> {
  return {
    ...createPatchBase(userId, scormPackage.versionId + 1),
    patch: {
      action: {
        type: PatchType.subst,
        x: scormPackage.name,
        y: title,
      },
      position: "name",
    },
  };
}

export function modifyScormPackageStatePatch(
  scormPackage: IScormPackage,
  userId: string,
  state: string
): IClientPatch<IPatchSubstAction<string>> {
  return {
    ...createPatchBase(userId, scormPackage.versionId + 1),
    patch: {
      action: {
        type: PatchType.subst,
        x: scormPackage.state,
        y: state,
      },
      position: "state",
    },
  };
}

export function modifyExpectedNumUsersPatch(
  scormPackage: IScormPackage,
  userId: string,
  expectedNumUsers: number | null
): IClientPatch<IPatchSubstAction<number | null>> {
  return {
    ...createPatchBase(userId, scormPackage.versionId + 1),
    patch: {
      action: {
        type: PatchType.subst,
        x: scormPackage.expectedNumUsers,
        y: expectedNumUsers,
      },
      position: "expectedNumUsers",
    },
  };
}

export function toggleUseCustomCompletionExitTextPatch(
  scormPackage: IScormPackage,
  userId: string,
  useCustomCompletedExitText: boolean
): IClientPatch<IPatchSubstAction<boolean>> {
  return {
    ...createPatchBase(userId, scormPackage.versionId + 1),
    patch: {
      action: {
        type: PatchType.subst,
        x: scormPackage.useCustomCompletedExitText,
        y: useCustomCompletedExitText,
      },
      position: "useCustomCompletedExitText",
    },
  };
}

export function toggleUseCustomInProgressExitTextPatch(
  scormPackage: IScormPackage,
  userId: string,
  useCustomInProgressExitText: boolean
): IClientPatch<IPatchSubstAction<boolean>> {
  return {
    ...createPatchBase(userId, scormPackage.versionId + 1),
    patch: {
      action: {
        type: PatchType.subst,
        x: scormPackage.useCustomInProgressExitText,
        y: useCustomInProgressExitText,
      },
      position: "useCustomInProgressExitText",
    },
  };
}

export function modifyInProgressExitTextPatch(
  scormPackage: IScormPackage,
  userId: string,
  locale: PreferredLocale,
  message: string
): IClientPatch<IPatchAddAction<LocalizedText> | IPatchSubstAction<string>> {
  const basePatch = createPatchBase(userId, scormPackage.versionId + 1);
  const existingMessage = scormPackage.inProgressExitText.elems.find(m => m.locale === locale);

  if (existingMessage) {
    const idx = scormPackage.inProgressExitText.elems.findIndex(m => m.locale === locale);
    return {
      ...basePatch,
      patch: substPatch(existingMessage.value.text, message, `inProgressExitText.${idx}.value.text`),
    };
  }

  const at = scormPackage.inProgressExitText.nextId;
  const item: LocalizedText = {
    id: at,
    locale,
    value: {
      text: message,
    },
  };
  return {
    ...basePatch,
    patch: addPatch(item, at, "inProgressExitText"),
  };
}

export const modifyCompletedExitTextPatch = (
  scormPackage: IScormPackage,
  userId: string,
  locale: PreferredLocale,
  message: string
): IClientPatch<IPatchAddAction<LocalizedText> | IPatchSubstAction<string>> => {
  const basePatch = createPatchBase(userId, scormPackage.versionId + 1);
  const existingMessage = scormPackage.completedExitText.elems.find(m => m.locale === locale);

  if (existingMessage) {
    const idx = scormPackage.completedExitText.elems.findIndex(m => m.locale === locale);
    return {
      ...basePatch,
      patch: substPatch(existingMessage.value.text, message, `completedExitText.${idx}.value.text`),
    };
  }

  const at = scormPackage.completedExitText.nextId;
  const item: LocalizedText = {
    id: at,
    locale,
    value: {
      text: message,
    },
  };
  return {
    ...basePatch,
    patch: addPatch(item, at, "completedExitText"),
  };
};

export function modifyUseStrictModePatch(
  scormPackage: IScormPackage,
  userId: string,
  useStrictMode: boolean
): IClientPatch<IPatchSubstAction<boolean>> {
  return {
    ...createPatchBase(userId, scormPackage.versionId + 1),
    patch: {
      action: {
        type: PatchType.subst,
        x: scormPackage.useStrictMode,
        y: useStrictMode,
      },
      position: "courseInstEnv.useStrictMode",
    },
  };
}

export function toggleAllowCourseReviewPatch(
  scormPackage: IScormPackage,
  userId: string,
  allowCourseReview: boolean
): IClientPatch<IPatchSubstAction<boolean>> {
  return {
    ...createPatchBase(userId, scormPackage.versionId + 1),
    patch: {
      action: {
        type: PatchType.subst,
        x: scormPackage.allowCourseReview,
        y: allowCourseReview,
      },
      position: "courseInstEnv.allowCourseReview",
    },
  };
}

export function toggleIncludeBrandingPatch(
  scormPackage: IScormPackage,
  userId: string,
  includeBranding: boolean
): IClientPatch<IPatchSubstAction<boolean>> {
  return {
    ...createPatchBase(userId, scormPackage.versionId + 1),
    patch: {
      action: {
        type: PatchType.subst,
        x: scormPackage.includeBranding,
        y: includeBranding,
      },
      position: "courseInstEnv.includeBranding",
    },
  };
}

export function toggleLockPackagePatch(
  scormPackage: IScormPackage,
  userId: string,
  lockPackage: boolean
): IClientPatch<IPatchSubstAction<boolean>> {
  return {
    ...createPatchBase(userId, scormPackage.versionId + 1),
    patch: {
      action: {
        type: PatchType.subst,
        x: scormPackage.locked,
        y: lockPackage,
      },
      position: "locked",
    },
  };
}
